import React from "react";
import Button from "@mui/material/Button";
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { UserResource } from "../../../../api/users";

interface AssociateStripeSubFormProps {
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const AssociateStripeSubForm: React.FC<AssociateStripeSubFormProps> = ({
  userId,
  onClose,
  onSuccess,
}) => {
  function handleClose() {
    formik.resetForm();
    onClose();
  }

  const formik = useFormik({
    initialValues: { subscriptionId: "" },
    onSubmit: async ({ subscriptionId }) => {
      try {
        await UserResource.postReceipt(userId, subscriptionId.trim());
        onSuccess();
        handleClose();
      } catch (e) {
        formik.setErrors({
          subscriptionId: `Failed to associate subscription. Error: ${e}`,
        });
      }
    },
    validate: ({ subscriptionId }) => {
      if (!subscriptionId.trim()) {
        return { subscriptionId: "Subscription ID is required" };
      }
      if (!subscriptionId.trim().startsWith("sub_")) {
        return { subscriptionId: "Must be a valid Stripe subscription ID (starts with sub_)" };
      }
    },
  });

  return (
    <>
      <DialogTitle>
        Associate Stripe Subscription
        <Typography variant="subtitle1" color="textSecondary" component="p">
          Paste the Stripe subscription ID to link it to this user via RevenueCat.
        </Typography>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, padding: "1rem" }}
        >
          <TextField
            id="subscriptionId"
            name="subscriptionId"
            label="Stripe Subscription ID"
            placeholder="sub_xxx..."
            value={formik.values.subscriptionId}
            onChange={formik.handleChange}
            required
            fullWidth
            error={Boolean(formik.errors.subscriptionId)}
            helperText={formik.touched.subscriptionId && formik.errors.subscriptionId}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
            {formik.isSubmitting ? <CircularProgress size={20} /> : "Associate"}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};
